import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import Vue from 'vue';
import { isRobot } from '@/js/helpers/robot';

const blackListUrls = [
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extensions:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
];

// You can set error message here or the filename to ignore
const ignoreErrors = [
  // Random plugins/extensions
  'top.GLOBALS',
  // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  'originalCreateNotification',
  'canvas.contentDocument',
  'MyApp_RemoveAllHighlights',
  'http://tt.epicplay.com',
  'Can\'t find variable: ZiteReader',
  'jigsaw is not defined',
  'ComboSearch is not defined',
  'http://loading.retry.widdit.com/',
  'atomicFindClose',
  // Facebook borked
  'fb_xd_fragment',
  // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
  // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  'bmi_SafeAddOnload',
  'EBCallBackMessageReceived',
  // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  'conduitPage',
  'r\\[Xa\\+"Delay"\\]',
  'r\\[Xa \\+ "Delay"\\]',
  // Iframe CORS errors
  'Blocked a frame with origin',
  // No local storage available has fail over cookie storage thus ignoring the issue
  'QuotaExceededError',
  // FB Messenger
  'xfbml\.customerchat\.js',
  'Response timeout',
  'Request has been terminated',
  'Request has been terminated\nPossible causes: the network is offline, Origin is not allowed by Access-Control-Allow-Origin, the page is being unloaded, etc.',
  'Request.prototype.crossDomainError',
];

if ((process.env.VUE_APP_SENTRY_TEST || process.env.NODE_ENV === 'production') && !isRobot()) {
  const liveDsn = process.env.VUE_APP_SENTRY_DSN;
  const dsn = process.env.VUE_APP_SENTRY_TEST || liveDsn;

  Sentry.init({
    dsn,
    integrations: [new Integrations.Vue({ Vue, attachProps: true })],
    blacklistUrls: blackListUrls,
    beforeSend: event => {
      // skipping ignore list for Sentry.captureMessage, because
      // it doesn't have the exception object
      if (!event.exception) {
        return event;
      }

      // Ignoring Internet Explorer version 1 - 10
      // (IE11 has different user agent and is not catching in this test).
      // Useful information: https://msdn.microsoft.com/pl-pl/library/hh869301(v=vs.85).aspx
      const ieFrom1To10 = /^.+MSIE \d{1,2}\.0.+$/.test(window.navigator.userAgent);
      const operaMini = /^.+Opera Mini.+$/.test((window.navigator.userAgent));

      const ignore = ignoreErrors
        .some(shouldIgnore => {
          const issue = new RegExp(`^.*${shouldIgnore}.*$`);

          return event.exception.values.some(exception => {
            // H27 errors reported on heroku https://help.heroku.com/VX2J01DE/why-do-h27-errors-happen
            const isUndefinedValue = exception.value === 'undefined' || exception.value === undefined;

            const exceptionVal = issue.test(exception.value);
            const exceptionFileName = exception.stacktrace
                                      && exception.stacktrace.frames
                                      && exception.stacktrace.frames
                                        .some(frame => issue.test(frame.filename));

            return isUndefinedValue || exceptionVal || exceptionFileName;
          });
        });

      return !(ieFrom1To10 || operaMini || ignore) ? event : null;
    },
  });
}

export default Sentry;
